/**
 * TrainingWheelsHint: a short-lived hint for someone new to a screen.
 *
 * It slides in over a spot that is empty for now, says what will appear there
 * and when, then gets out of the way. It is for first-time orientation, not
 * for anything the user must read, so it never blocks, never needs dismissing
 * and stops appearing once it has done its job.
 *
 *   <TrainingWheelsHint
 *     storageKey="candidate-interview-transcript"
 *     message="Your interview transcripts will appear here once ..."
 *     anchor={<Text type="secondary">No interview yet</Text>}
 *     enabled={!interviewCompleted}
 *   />
 *
 * `anchor` is what is in that spot all the time. The hint covers it while it
 * is up and uncovers it afterwards. Put the quiet, always-true statement
 * there ("No interview yet"), so the spot is never left silently blank once
 * the hint has gone. The hint is not a substitute for that line.
 *
 * When it shows
 *   - `enabled` is true. The caller owns the condition. Pass false while data
 *     is still loading, so a hint never claims something is absent before the
 *     answer has arrived.
 *   - This user has seen it fewer than `showCap` times (default 3) and has not
 *     been marked done with `markHintDone(storageKey)`. Counts are kept per
 *     user and per `storageKey` in localStorage (see ./storage).
 *   - If storage cannot be read or written, it shows at most once per page
 *     load rather than on every visit.
 *   Each mount that actually shows the hint counts as one showing.
 *
 * How it behaves
 *   - Slides in from the left over the anchor, and out again after
 *     `durationMs` (default 5000).
 *   - Pauses while the pointer is over it or focus is inside it, and resumes
 *     with the time it had left, but never less than 1.5 seconds.
 *   - Closes immediately with the X button or the Escape key.
 *   - With prefers-reduced-motion it fades instead of sliding.
 *   - Announced politely to assistive technology: the wrapper is an
 *     aria-live="polite" region that exists before the hint is inserted.
 *
 * Storage key
 *   One per hint, stable and descriptive. Changing a key re-shows that hint to
 *   everyone, which is also how to deliberately re-teach something that
 *   changed. Call `markHintDone(storageKey)` when the user does the thing the
 *   hint was teaching, so it stops for them straight away.
 *
 * Colours, type and radii come from the brand tokens in styles/tokens.css.
 */

import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import styles from './index.module.css';
import { recordHintShown, shouldShowHint } from './storage';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';

export { markHintDone } from './storage';

export const DEFAULT_DURATION_MS = 5000;
export const DEFAULT_SHOW_CAP = 3;

/** Long enough for the exit animation to finish before the hint unmounts. */
const EXIT_MS = 200;
/** A hint released after a long hover should not vanish the instant it is. */
const MIN_RESUME_MS = 1500;

type Status = 'hidden' | 'open' | 'closing';

export interface TrainingWheelsHintProps {
  /** What to tell the user. One or two plain sentences. */
  message: string;
  /** Unique, stable id for this hint. Also the per-user storage key. */
  storageKey: string;
  /** What is in this spot all the time. The hint covers it, then uncovers it. */
  anchor: ReactNode;
  /** Whether the hint is allowed to show at all. Defaults to true. */
  enabled?: boolean;
  /** How long it stays up, in milliseconds. Defaults to 5000. */
  durationMs?: number;
  /** How many times one user sees it before it stops. Defaults to 3. */
  showCap?: number;
}

export const TrainingWheelsHint = memo(
  ({
    message,
    storageKey,
    anchor,
    enabled = true,
    durationMs = DEFAULT_DURATION_MS,
    showCap = DEFAULT_SHOW_CAP,
  }: TrainingWheelsHintProps) => {
    const reducedMotion = usePrefersReducedMotion();
    const [status, setStatus] = useState<Status>('hidden');
    const [hovered, setHovered] = useState(false);
    const [focused, setFocused] = useState(false);
    const paused = hovered || focused;

    // A mount is counted once. This also keeps React StrictMode's second
    // effect pass in development from counting it twice.
    const counted = useRef(false);
    const remaining = useRef(durationMs);
    const startedAt = useRef(0);

    useEffect(() => {
      if (!enabled || counted.current) return;
      if (!shouldShowHint(storageKey, showCap)) return;
      counted.current = true;
      recordHintShown(storageKey);
      remaining.current = durationMs;
      setStatus('open');
    }, [enabled, storageKey, showCap, durationMs]);

    useEffect(() => {
      if (!enabled) setStatus('hidden');
    }, [enabled]);

    const close = useCallback(
      () => setStatus((current) => (current === 'open' ? 'closing' : current)),
      [],
    );

    useEffect(() => {
      if (status !== 'open' || paused) return;
      startedAt.current = Date.now();
      const timer = window.setTimeout(close, remaining.current);
      return () => {
        window.clearTimeout(timer);
        remaining.current = Math.max(
          remaining.current - (Date.now() - startedAt.current),
          MIN_RESUME_MS,
        );
      };
    }, [status, paused, close]);

    useEffect(() => {
      if (status !== 'closing') return;
      const timer = window.setTimeout(() => setStatus('hidden'), EXIT_MS);
      return () => window.clearTimeout(timer);
    }, [status]);

    const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
        setFocused(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') close();
    };

    return (
      <div className={styles.root} aria-live="polite">
        <div className={styles.anchor}>{anchor}</div>
        {status !== 'hidden' && (
          <div
            className={styles.hint}
            data-state={status}
            data-motion={reducedMotion ? 'reduced' : 'full'}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onFocus={() => setFocused(true)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
          >
            <svg
              className={styles.icon}
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
              focusable="false"
            >
              <circle
                cx="8"
                cy="8"
                r="7"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M8 7.25v4M8 4.75v.01"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            <p className={styles.message}>{message}</p>
            <button
              type="button"
              className={styles.close}
              aria-label="Dismiss hint"
              onClick={close}
            >
              <svg
                viewBox="0 0 12 12"
                width="12"
                height="12"
                fill="none"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M2 2l8 8M10 2l-8 8"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    );
  },
);

TrainingWheelsHint.displayName = 'TrainingWheelsHint';

export default TrainingWheelsHint;
